import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// Both stamps come from this repo; the player stamp names it explicitly, the web one does not.
const DEFAULT_REPOSITORY = "twistgs/Conveyor";
export const shortSha = (sha) => sha.slice(0, 8);
/** "DEV_502" when the commit carries the tag itself, "DEV_502 +3" three commits later, "none". */
export function tagLabel(tag, since) {
    if (!tag)
        return "none";
    return since ? `${tag} +${since}` : tag;
}
/** ISO instant → "2026-10-09 11:02 UTC", matching the host build stamp's format. */
export function formatUtc(iso) {
    if (!iso)
        return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime()))
        return iso;
    return `${d.toISOString().slice(0, 16).replace("T", " ")} UTC`;
}
/** 1274 → "21m 14s", 3720 → "1h 02m". */
export function formatDuration(seconds) {
    if (seconds == null || seconds < 0)
        return null;
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0)
        return `${h}h ${String(m).padStart(2, "0")}m`;
    if (m > 0)
        return `${m}m ${String(s).padStart(2, "0")}s`;
    return `${s}s`;
}
function CommitLink({ sha, repository }) {
    return (_jsx("a", { className: "font-mono text-slate-300 underline decoration-slate-600 hover:text-cyan-400", href: `https://github.com/${repository ?? DEFAULT_REPOSITORY}/commit/${sha}`, target: "_blank", rel: "noreferrer", title: sha, children: shortSha(sha) }));
}
function RunLink({ player }) {
    if (player.runNumber == null)
        return null;
    const label = `run #${player.runNumber}`;
    if (player.runId == null)
        return _jsx(_Fragment, { children: label });
    return (_jsx("a", { className: "underline decoration-slate-600 hover:text-cyan-400", href: `https://github.com/${player.repository ?? DEFAULT_REPOSITORY}/actions/runs/${player.runId}`, target: "_blank", rel: "noreferrer", children: label }));
}
/** Joins the non-empty parts with a middle dot. */
function dotted(parts) {
    const kept = parts.filter((p) => p !== null && p !== undefined && p !== false && p !== "");
    return kept.flatMap((p, i) => (i === 0 ? [p] : [" · ", p]));
}
function playerBuilt(player) {
    const at = formatUtc(player.buildFinishedAt ?? player.buildStartedAt);
    const took = formatDuration(player.buildDurationSeconds);
    if (at && took)
        return `built ${at} in ${took}`;
    if (at)
        return `built ${at}`;
    if (took)
        return `build took ${took}`;
    return null;
}
/** Footer: one line for the host, one for the player. */
export function BuildLines({ info }) {
    const web = info.web;
    const player = info.player;
    return (_jsxs(_Fragment, { children: [_jsxs("p", { children: ["Web:", " ", dotted([
                        _jsx("span", { className: "font-mono text-slate-300", children: info.build }, "d"),
                        web?.commit && _jsx(CommitLink, { sha: web.commit, repository: null }, "c"),
                        web?.branch,
                        web?.runNumber && `run #${web.runNumber}`,
                    ])] }), _jsxs("p", { children: ["Game:", " ", player
                        ? dotted([
                            player.commit && _jsx(CommitLink, { sha: player.commit, repository: player.repository }, "c"),
                            player.branch,
                            `dev ${tagLabel(player.devTag, player.commitsSinceDevTag)}`,
                            `prod ${tagLabel(player.prodTag, player.commitsSinceProdTag)}`,
                            playerBuilt(player),
                            _jsx(RunLink, { player: player }, "r"),
                        ])
                        : "no build stamp (local player, or built before stamping)"] })] }));
}
function Row({ label, children }) {
    return (_jsxs("div", { className: "flex items-baseline justify-between gap-4 py-1", children: [_jsx("span", { className: "shrink-0 text-slate-400", children: label }), _jsx("span", { className: "min-w-0 truncate text-right text-slate-300", children: children })] }));
}
/** Welcome page: the same facts as labelled rows, one block for the host and one for the player. */
export function BuildPanel({ info }) {
    const web = info?.web;
    const player = info?.player;
    return (_jsxs("div", { className: "grid gap-3 text-xs sm:grid-cols-2", children: [_jsxs("div", { className: "rounded-lg border border-slate-800 bg-slate-800/40 px-4 py-2.5", children: [_jsx("h3", { className: "mb-1 text-sm font-semibold text-slate-200", children: "Web build" }), _jsx(Row, { label: "Built", children: _jsx("span", { className: "font-mono", children: info?.build ?? "…" }) }), _jsx(Row, { label: "Commit", children: web?.commit ? _jsx(CommitLink, { sha: web.commit, repository: null }) : "—" }), _jsx(Row, { label: "Branch", children: web?.branch ?? "—" }), _jsx(Row, { label: "Run", children: web?.runNumber ? `#${web.runNumber}` : "—" })] }), _jsxs("div", { className: "rounded-lg border border-slate-800 bg-slate-800/40 px-4 py-2.5", children: [_jsx("h3", { className: "mb-1 text-sm font-semibold text-slate-200", children: "Game (Unity player)" }), !info ? (_jsx("p", { className: "py-1 text-slate-400", children: "\u2026" })) : !player ? (_jsx("p", { className: "py-1 text-slate-400", children: "No build stamp \u2014 a local player, or one built before full builds stamped it." })) : (_jsxs(_Fragment, { children: [_jsxs(Row, { label: "Commit", children: [player.commit ? _jsx(CommitLink, { sha: player.commit, repository: player.repository }) : "—", player.commitSubject && _jsx("span", { className: "ml-2 text-slate-400", title: player.commitSubject, children: player.commitSubject })] }), _jsx(Row, { label: "Branch", children: player.branch ?? "—" }), _jsx(Row, { label: "Latest dev tag", children: tagLabel(player.devTag, player.commitsSinceDevTag) }), _jsx(Row, { label: "Latest prod tag", children: tagLabel(player.prodTag, player.commitsSinceProdTag) }), _jsx(Row, { label: "Built", children: formatUtc(player.buildFinishedAt) ?? "—" }), _jsx(Row, { label: "Build took", children: formatDuration(player.buildDurationSeconds) ?? "—" }), _jsxs(Row, { label: "Run", children: [_jsx(RunLink, { player: player }), player.actor && _jsxs("span", { className: "text-slate-400", children: [" by ", player.actor] })] })] }))] })] }));
}
